<template>
  <div>
    <menu-content-container
      :header="$t('forms.button_text')"
      :padding-left-right="8"
    >
      <content-row v-if="mode === 'static'">
        <t-switch
          v-model="syncButtonText"
          data-testid="sync-button-text-switch"
          :label="$t('api_block.sync_button_text')"
        ></t-switch>
      </content-row>
      <content-row v-if="mode === 'dynamic'">
        <TInput
          v-model="textContent"
          data-testid="button-text-input"
          :label="$t('forms.text')"
        ></TInput>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.button_align')"
      :padding-left-right="8"
    >
      <content-row justify-between>
        <TButtonGroup data-testid="product-align-button-group">
          <TGroupButton
            data="left"
            data-testid="button-align-left"
            small
            :active="align === 'left'"
            @button-clicked="align = 'left'"
          >
            <AligmentLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="button-align-center"
            small
            :active="align === 'center'"
            @button-clicked="align = 'center'"
          >
            <AligmentCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="button-align-right"
            small
            :active="align === 'right'"
            @button-clicked="align = 'right'"
          >
            <AligmentRight />
          </TGroupButton>
        </TButtonGroup>
        <t-switch
          v-model="fullWidth"
          :label="$t('forms.full_width')"
          data-testid="full-width-switch"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.button_style')"
      :padding-left-right="8"
    >
      <content-row>
        <color-picker
          data-testid="product-button-color"
          :label="$t('product.btn_color')"
          product
          :prodstructure="prodstructure"
          :default-color="bgColor"
          @color-change="bgColor = $event"
        ></color-picker>
        <color-picker
          data-testid="product-button-text-color"
          :label="$t('product.btn_text_color')"
          position="right"
          product
          :prodstructure="prodstructure"
          :default-color="color"
          @color-change="color = $event"
        ></color-picker>
      </content-row>
      <content-row class="!block" data-testid="gradient-form">
        <TButton
          v-if="!gradient"
          data-testid="add-gradient"
          @click="addGradient"
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
        <TTabsToggle
          v-if="gradient"
          :header="$t('forms.gradient')"
          default-open
          no-move
          show-remove
          @remove-block="gradient = undefined"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="gradient" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>

      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.button_hover')"
      :padding-left-right="8"
      border-full
    >
      <template #header-action>
        <div
          class="flex cursor-pointer items-center font-inter text-xs font-medium leading-6 text-accent-1 transition duration-300 hover:opacity-80"
          data-testid="toggle-hover-button"
          @click="toggleHover"
        >
          <div>
            {{ !hover ? $t("forms.add_hover") : $t("forms.remove_hover") }}
          </div>
          <PlusIcon v-if="!hover" />
          <Minus v-else />
        </div>
      </template>
      <transition name="slide-fade-borders" mode="out-in">
        <div v-if="hover">
          <content-row>
            <color-picker
              :label="$t('forms.button_color_hover')"
              data-testid="hover-button-color"
              position="left"
              :default-color="hoverBgColor"
              @color-change="hoverBgColor = $event"
            ></color-picker>
            <color-picker
              :label="$t('forms.button_text_color_hover')"
              data-testid="hover-button-text-color"
              position="right"
              :default-color="hoverColor"
              @color-change="hoverColor = $event"
            ></color-picker>
          </content-row>
          <content-row class="!block">
            <TButton
              v-if="!hoverGradient"
              data-testid="add-hover-gradient"
              @click="addGradientHover"
            >
              <template #icon>
                <svg
                  width="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                    fill="currentColor"
                  />
                </svg>
              </template>
              {{ $t("forms.add_gradient") }}
            </TButton>
            <TTabsToggle
              v-if="hoverGradient"
              :header="$t('forms.gradient_hover')"
              data-testid="hover-gradient-form"
              default-open
              no-move
              show-remove
              @remove-block="hoverGradient = 'none'"
            >
              <menu-content-container border-full :padding-left-right="8">
                <TGradient v-model="hoverGradient" />
              </menu-content-container>
            </TTabsToggle>
          </content-row>
        </div>
      </transition>

      <content-row :margin-top="12">
        <t-note>{{ $t("layout.hover_text") }}</t-note>
      </content-row>
    </menu-content-container>

    <borderRadiusForm
      data-testid="product-button-radius"
      :label="$t('forms.button_border')"
      :prodstructure="prodstructure"
      :product="true"
      :padding-left-right="8"
      prefix="button-"
    ></borderRadiusForm>

    <menu-content-container :padding-left-right="8">
      <FontSizeColor block-name="button" :prodstructure="prodstructure" />
      <content-row :margin-top="8">
        <TButtonGroup data-testid="product-text-align-button-group">
          <TGroupButton
            data="left"
            data-testid="button-text-align-left"
            small
            :active="textAlign === 'left'"
            @button-clicked="textAlign = 'left'"
          >
            <TextLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="button-text-align-center"
            small
            :active="textAlign === 'center'"
            @button-clicked="textAlign = 'center'"
          >
            <TextCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="button-text-align-right"
            small
            :active="textAlign === 'right'"
            @button-clicked="textAlign = 'right'"
          >
            <TextRight />
          </TGroupButton>
        </TButtonGroup>
      </content-row>
    </menu-content-container>

    <paddingForm
      data-testid="button-padding-form"
      :product="true"
      :prodstructure="prodstructure"
      :label="$t('forms.button_padding')"
      :input-label="$t('forms.padding_label')"
      :toggle-label="$t('forms.independent_paddings')"
      :padding-left-right="8"
      prefix="button-inner-"
    ></paddingForm>

    <paddingForm
      :product="true"
      :prodstructure="prodstructure"
      :padding-left-right="8"
      :border-bottom="false"
      class="!mb-2"
      prefix="button-"
    ></paddingForm>
  </div>
</template>

<script lang="ts" setup>
  import paddingForm from "../paddingForm.vue";
  import borderRadiusForm from "../borderRadiusForm.vue";
  import FontSizeColor from "./fontSizeColorProduct.vue";
  import colorPicker from "../elements/colorPicker.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";

  import {
    AligmentLeft,
    AligmentCenter,
    AligmentRight,
    TextLeft,
    TextCenter,
    TextRight,
    PlusIcon,
    Minus,
  } from "@topol/icons";
  import {
    TButtonGroup,
    TGroupButton,
    TSwitch,
    TTabsToggle,
    TNote,
    TButton,
    TInput,
  } from "@topol/twig-ui";

  import { computed } from "vue";
  import { TopolFeedBlock } from "@/schemas/template/blocks/feedBlockSchema";

  const mjmlStore = useMjmlStore();

  const activeBlock = computed(() => {
    return mjmlStore.getSelectedBlock as TopolFeedBlock | undefined;
  });

  const prodstructure = computed(() => {
    const prodstructure = mjmlStore.getActiveBlockAttrString("prodstructure");
    if (prodstructure) {
      return prodstructure;
    }
    return false;
  });

  const mode = computed(() => {
    return mjmlStore.getActiveBlockPropString("mode") || "static";
  });

  const syncButtonText = computed({
    get() {
      return activeBlock.value?.syncButtonText ?? true;
    },
    set(val: boolean) {
      mjmlStore.updateBlockProp({
        prop: "syncButtonText",
        value: val,
      });
    },
  });

  const textContent = computed({
    get() {
      if (!activeBlock.value) {
        return "";
      }
      const items = mjmlStore.getActiveBlockFeedItems(activeBlock.value);
      const item = items?.[0];
      if (!item) {
        return "";
      }
      const raw = item["button"] || "";
      const doc = new DOMParser().parseFromString(raw, "text/html");
      return doc.body.textContent || "";
    },
    set(val: string) {
      if (!activeBlock.value) return;
      const items = mjmlStore.getActiveBlockFeedItems(activeBlock.value);
      if (!items?.length) return;
      const sync = activeBlock.value?.syncButtonText ?? true;
      const applyToAll =
        sync &&
        !!activeBlock.value.blockName &&
        !!mjmlStore.mjml.feedBlocksApplyToAll[activeBlock.value.blockName];

      if (applyToAll) {
        mjmlStore.updateAllSiblingFeedBlockItemsAttribute({
          blockName: activeBlock.value.blockName,
          prop: "button",
          value: val,
          prodstructure: prodstructure.value,
        });
        return;
      }

      mjmlStore.updateBlockItemAttribute({
        prop: "button",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
        itemIndex: 0,
      });
    },
  });

  const color = computed({
    get() {
      return mjmlStore.getActiveBlockAttrString("button-color");
    },
    set(val: string) {
      mjmlStore.updateBlockAttribute({
        prop: "button-color",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const bgColor = computed({
    get() {
      return mjmlStore.getActiveBlockAttrString("button-background-color");
    },
    set(val: string) {
      mjmlStore.updateBlockAttribute({
        prop: "button-background-color",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const gradient = computed({
    get() {
      return mjmlStore.getActiveBlockAttrString("button-gradient");
    },
    set(val?: string) {
      mjmlStore.updateBlockAttribute({
        prop: "button-gradient",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const align = computed({
    get() {
      return mjmlStore.getActiveBlockAttrString("button-align");
    },
    set(val: string) {
      mjmlStore.updateBlockAttribute({
        prop: "button-align",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const textAlign = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString("button-text-align") || "center"
      );
    },
    set(val: string) {
      mjmlStore.updateBlockAttribute({
        prop: "button-text-align",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const fullWidth = computed({
    get() {
      return mjmlStore.getActiveBlockAttrString("button-width") == "100%";
    },
    set(val: boolean) {
      let value = "100%";
      if (val == false) {
        value = "auto";
      } else {
        mjmlStore.updateBlockAttribute({
          prop: "button-align",
          value: "center",
        });
      }
      mjmlStore.updateBlockAttribute({
        prop: "button-width",
        value: value,
      });
    },
  });

  const hover = computed({
    get() {
      return mjmlStore.getActiveBlockPropString("button-hover");
    },
    set(val?: Record<string, string>) {
      mjmlStore.updateBlockProp({
        prop: "button-hover",
        value: val,
      });
    },
  });

  const hoverBgColor = computed({
    get() {
      return hover.value?.["background-color"] || bgColor.value;
    },
    set(val: string) {
      const newHover = { ...hover.value, "background-color": val };
      hover.value = newHover;
    },
  });

  const hoverColor = computed({
    get() {
      return hover.value?.color || color.value;
    },
    set(val: string) {
      const newHover = { ...hover.value, color: val };
      hover.value = newHover;
    },
  });

  const hoverGradient = computed({
    get() {
      if (hover.value?.gradient === "none") {
        return undefined;
      }
      return hover.value?.gradient || gradient.value;
    },
    set(val?: string) {
      if (!val) {
        const newHover = { ...hover.value };
        delete newHover.gradient;
        hover.value = newHover;
        return;
      }
      const newHover = { ...hover.value, gradient: val };
      hover.value = newHover;
    },
  });

  function toggleHover() {
    if (hover.value) {
      hover.value = undefined;
    } else {
      hover.value = {};
    }
  }

  function addGradient() {
    gradient.value = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  function addGradientHover() {
    hoverGradient.value = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }
</script>
